import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Upload,
  User,
  ShieldCheck,
  Building,
  Calendar,
  PenTool,
  CreditCard,
  Copy,
  Check,
  QrCode,
  Sparkles,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { uploadFile } from "@/lib/uploadFile";
import { validateUploadFile, MAX_AVATAR_LOGO_SIZE, extractErrorMessage } from "@/lib/utils";

export interface DigitalEmployeeBadgeProps {
  user: {
    id?: string;
    fullName?: string;
    employeeCode?: string;
    position?: string;
    department?: string;
    email?: string;
    phone?: string;
    avatar?: string | null;
    signature?: string | null;
    bankName?: string | null;
    bankAccountNumber?: string | null;
    bankBranch?: string | null;
    status?: string;
    joinDate?: string | Date | null;
  };
  isEditing?: boolean;
  onAvatarChange?: (url: string) => void;
  onSignatureChange?: (url: string) => void;
  className?: string;
}

export const DigitalEmployeeBadge: React.FC<DigitalEmployeeBadgeProps> = ({
  user,
  isEditing = false,
  onAvatarChange,
  onSignatureChange,
  className = "",
}) => {
  const [copiedBank, setCopiedBank] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isUploadingSignature, setIsUploadingSignature] = useState(false);

  // Generate vCard string for QR Code
  const vCardString = `BEGIN:VCARD
VERSION:3.0
FN:${user.fullName || "Nhân viên APV"}
ORG:Công ty TNHH Artpresto Việt Nam
TITLE:${user.position || ""}
TEL;TYPE=CELL:${user.phone || ""}
EMAIL:${user.email || ""}
NOTE:Mã NV: ${user.employeeCode || "—"} - Phòng: ${user.department || "—"}
END:VCARD`;

  const handleCopyBank = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user.bankAccountNumber) return;
    navigator.clipboard.writeText(user.bankAccountNumber.replace(/\s+/g, ""));
    setCopiedBank(true);
    toast.success(`Đã sao chép số tài khoản: ${user.bankAccountNumber}`);
    setTimeout(() => setCopiedBank(false), 2000);
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateUploadFile(file, MAX_AVATAR_LOGO_SIZE, "Ảnh đại diện");
    if (!validation.valid) {
      toast.error(validation.error);
      e.target.value = "";
      return;
    }
    try {
      setIsUploadingAvatar(true);
      const res = await uploadFile(file, { preservePng: true });
      onAvatarChange?.(res.url);
      toast.success("Tải ảnh đại diện thành công!");
    } catch (err: any) {
      toast.error(`Lỗi tải ảnh: ${extractErrorMessage(err)}`);
    } finally {
      setIsUploadingAvatar(false);
      e.target.value = "";
    }
  };

  const handleSignatureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateUploadFile(file, MAX_AVATAR_LOGO_SIZE, "Mẫu chữ ký");
    if (!validation.valid) {
      toast.error(validation.error);
      e.target.value = "";
      return;
    }
    try {
      setIsUploadingSignature(true);
      const res = await uploadFile(file, { preservePng: true });
      onSignatureChange?.(res.url);
      toast.success("Tải mẫu chữ ký điện tử thành công!");
    } catch (err: any) {
      toast.error(`Lỗi tải chữ ký: ${extractErrorMessage(err)}`);
    } finally {
      setIsUploadingSignature(false);
      e.target.value = "";
    }
  };

  const initials = (user.fullName || "NV")
    .split(" ")
    .filter(Boolean)
    .slice(-2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

  return (
    <div className={`space-y-4 ${className}`}>
      {/* 1. Thẻ Định Danh Nhân Sự Cấp Cao (Digital ID Badge) */}
      <div className="relative rounded-2xl bg-white border border-slate-200/90 shadow-sm p-5 text-slate-800 transition-all hover:shadow-md overflow-hidden">
        {/* Top Header Strap Decoration */}
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-indigo-600 via-sky-500 to-indigo-800" />

        {/* Badge Header */}
        <div className="flex items-center justify-between pb-3 border-b border-slate-100">
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded bg-indigo-600 text-white flex items-center justify-center font-bold text-[10px]">
              A
            </div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
              Artpresto Vietnam
            </span>
          </div>

          <div className="flex items-center gap-1">
            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              {user.status === "ACTIVE" || !user.status ? "Đang làm việc" : user.status}
            </span>
          </div>
        </div>

        {/* Avatar & Identifiers */}
        <div className="pt-4 flex flex-col items-center text-center">
          <div className="relative group">
            <Avatar className="h-24 w-24 rounded-full ring-4 ring-slate-100 shadow-sm border border-slate-200">
              <AvatarImage src={user.avatar || undefined} alt={user.fullName} className="object-cover" />
              <AvatarFallback className="bg-indigo-600 text-white text-xl font-bold">
                {initials || "AP"}
              </AvatarFallback>
            </Avatar>

            {/* Avatar Upload Trigger */}
            <label className="absolute bottom-0 right-0 p-1.5 rounded-full bg-indigo-600 text-white shadow-md hover:bg-indigo-700 cursor-pointer transition-transform hover:scale-110">
              <Upload className="w-3.5 h-3.5" />
              <input
                type="file"
                accept="image/*"
                className="hidden"
                disabled={isUploadingAvatar}
                onChange={handleAvatarUpload}
              />
            </label>
          </div>

          <h3 className="mt-3 text-base sm:text-lg font-bold text-slate-900 tracking-tight">
            {user.fullName || "Họ và Tên Nhân Viên"}
          </h3>

          <div className="mt-1 flex items-center gap-1.5">
            <span className="font-mono text-[11px] font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded border border-slate-200/80">
              {user.employeeCode || "—"}
            </span>
            <span className="text-xs font-semibold text-indigo-600">
              {user.position || "Chưa thiết lập chức vụ"}
            </span>
          </div>

          <p className="mt-1 text-xs text-slate-500 flex items-center gap-1">
            <Building className="w-3 h-3 text-slate-400" />
            {user.department || "Chưa thiết lập phòng ban"}
          </p>

          {/* vCard QR Code Section */}
          <div className="mt-4 pt-3 border-t border-slate-100 w-full flex items-center justify-between gap-3 bg-slate-50/60 p-3 rounded-xl border border-slate-100">
            <div className="text-left space-y-0.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1">
                <QrCode className="w-3 h-3 text-indigo-600" /> Danh Bạ vCard
              </span>
              <p className="text-[11px] text-slate-600 font-medium leading-tight">
                Quét mã QR để lưu danh bạ trực tiếp vào điện thoại.
              </p>
            </div>
            <div className="p-1 bg-white rounded-lg border border-slate-200/80 shadow-xs shrink-0">
              <QRCodeSVG value={vCardString} size={64} level="M" />
            </div>
          </div>
        </div>
      </div>

      {/* 2. Khung Chữ Ký Tay / Chữ Ký Điện Tử (Mẫu Ký Tự Động Chứng Từ) */}
      <div className="rounded-xl bg-white border border-slate-200/90 shadow-sm p-4 space-y-2.5">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
            <PenTool className="w-3.5 h-3.5 text-indigo-600" />
            Mẫu Chữ Ký Số & Ký Tay
          </span>
          <label className="text-[11px] text-indigo-600 hover:text-indigo-800 font-semibold cursor-pointer flex items-center gap-1">
            <Upload className="w-3 h-3" />
            {user.signature ? "Thay đổi" : "Tải lên"}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              disabled={isUploadingSignature}
              onChange={handleSignatureUpload}
            />
          </label>
        </div>

        <div className="h-20 w-full rounded-lg border border-dashed border-slate-200 bg-slate-50/50 flex items-center justify-center p-2 overflow-hidden relative group">
          {user.signature ? (
            <img
              src={user.signature}
              alt="Mẫu chữ ký"
              className="max-h-full max-w-full object-contain filter contrast-125"
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
          ) : (
            <div className="text-center text-slate-400 text-xs">
              <p className="italic">Chưa có mẫu chữ ký điện tử</p>
              <p className="text-[10px] text-slate-400 mt-0.5">
                (Dùng tự động ký phiếu KCS, xuất kho, phê duyệt)
              </p>
            </div>
          )}
        </div>
      </div>

      {/* 3. Thẻ ATM Nhận Lương Mini (Payroll Card) */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-emerald-900 via-teal-900 to-slate-950 p-4 text-white shadow-md border border-emerald-700/40 select-none">
        <div className="flex items-center justify-between text-[10px] uppercase font-bold tracking-wider text-emerald-300">
          <span className="flex items-center gap-1">
            <CreditCard className="w-3.5 h-3.5" /> Thẻ Nhận Lương
          </span>
          <span className="text-slate-300 font-mono text-[9px]">PAYROLL CARD</span>
        </div>

        <div className="mt-2.5 flex items-center justify-between gap-2">
          <div>
            <div className="text-[9px] text-emerald-200/70 uppercase tracking-widest font-mono">
              Số tài khoản ngân hàng
            </div>
            <div className="font-mono text-sm sm:text-base font-black tracking-widest text-white">
              {user.bankAccountNumber || "Chưa thiết lập"}
            </div>
          </div>

          {user.bankAccountNumber && (
            <button
              type="button"
              onClick={handleCopyBank}
              title="Sao chép số tài khoản"
              className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              {copiedBank ? (
                <Check className="w-3.5 h-3.5 text-emerald-300" />
              ) : (
                <Copy className="w-3.5 h-3.5 text-amber-300" />
              )}
            </button>
          )}
        </div>

        <div className="mt-2 pt-2 border-t border-emerald-800/60 flex items-center justify-between text-[10px]">
          <div className="truncate text-emerald-100 font-medium">
            {user.bankName || "Ngân hàng thụ hưởng"}
          </div>
          <div className="shrink-0 text-emerald-300/80 font-mono text-[9px]">
            {user.bankBranch || ""}
          </div>
        </div>
      </div>
    </div>
  );
};
export default DigitalEmployeeBadge;
